/**
 * F32 (Wave 2C) — admin open-games moderation API client.
 *
 * Endpoints (F32 plan §7.2):
 *   GET    /admin/open-games                       → searchGames (paginated; status/sport/reported filters)
 *   GET    /admin/open-games/:id                   → getGame (detail + report history + join requests)
 *   POST   /admin/open-games/:id/suspend           → suspendGame (open_games:moderate)
 *   POST   /admin/open-games/:id/unsuspend         → unsuspendGame (open_games:moderate)
 *   GET    /admin/open-game-reports                → getReports (queue; status filter)
 *   POST   /admin/open-game-reports/:id/resolve    → resolveReport (open_games:moderate)
 *
 * Admin is suspend/view-only (D20) — no write to spots/level/price. Permission
 * gating happens server-side; the page-level <ProtectedRoute> gates navigation
 * (`open_games:read`) and the in-page action bar gates Suspend/Unsuspend/Resolve
 * on `open_games:moderate`.
 *
 * Component tests get the in-memory mock via the vitest alias in
 * `apps/admin/vitest.config.ts` rewriting `'./api/openGames'`.
 */
import type {
  AdminOpenGameListItem,
  OpenGameDetail,
  OpenGameReport,
  OpenGameReportStatus,
  OpenGameStatus,
  ResolveOpenGameReportInput,
  SuspendOpenGameInput,
} from '@bravemove/types';
import type { SportName } from '@bravemove/design-tokens/icons';
import { adminFetch, buildQuery } from '../auth/apiClient';

// Re-export the frozen read shapes the pages consume so they can import them
// from this client module (and transparently from the fixture via the alias).
export type { AdminOpenGameListItem, OpenGameDetail, OpenGameReport } from '@bravemove/types';

// ─── Response envelope shapes ────────────────────────────────────────────────

export interface AdminOpenGamesListResponse {
  readonly data: readonly AdminOpenGameListItem[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/** Admin detail = the public detail shape + the open-game's report history. */
export interface AdminOpenGameDetailResponse {
  readonly game: OpenGameDetail;
  readonly reports: readonly OpenGameReport[];
}

export interface AdminOpenGameReportsListResponse {
  readonly data: readonly OpenGameReport[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

export interface SearchGamesOpts {
  /** Lifecycle status filter; omit (or 'all') for every status. */
  readonly status?: OpenGameStatus | 'all';
  /** Sport filter. */
  readonly sport?: SportName;
  /** When true, only games with >=1 open report. */
  readonly reported?: boolean;
  /** When true, only admin-suspended games (suspended_at IS NOT NULL). */
  readonly suspended?: boolean;
  readonly page?: number;
  readonly perPage?: number;
}

export interface ListReportsOpts {
  readonly status?: OpenGameReportStatus | 'all';
  readonly page?: number;
  readonly perPage?: number;
}

interface DataEnvelope<T> {
  readonly data: T;
}

// ─── API surface ─────────────────────────────────────────────────────────────

/**
 * The `/admin/open-games[-reports]` routes support OFFSET pagination
 * (`page` / `perPage` → `{ data, pagination: { page, perPage, total } }` with
 * a real filtered COUNT — TGS-08, mirroring admin/skill-disputes). The client
 * always sends `page`+`perPage`, so the server always answers with the offset
 * envelope and the pages' `Pagination` control + the KPI/badge `perPage: 1`
 * count fetches read REAL totals (no synthesized `count + 1`).
 */
export const adminOpenGamesApi = {
  async searchGames(opts: SearchGamesOpts = {}): Promise<AdminOpenGamesListResponse> {
    const qs = buildQuery({
      status: opts.status === 'all' ? undefined : opts.status,
      sport: opts.sport,
      reportedOnly: opts.reported ? true : undefined,
      suspendedOnly: opts.suspended ? true : undefined,
      page: opts.page ?? 1,
      perPage: opts.perPage ?? 25,
    });
    return adminFetch<AdminOpenGamesListResponse>(`/admin/open-games${qs}`);
  },

  async getGame(id: string): Promise<AdminOpenGameDetailResponse | null> {
    try {
      const body = await adminFetch<DataEnvelope<AdminOpenGameDetailResponse>>(
        `/admin/open-games/${encodeURIComponent(id)}`,
      );
      return body.data;
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) return null;
      throw err;
    }
  },

  async suspendGame(id: string, input: SuspendOpenGameInput): Promise<OpenGameDetail> {
    const body = await adminFetch<DataEnvelope<OpenGameDetail>>(
      `/admin/open-games/${encodeURIComponent(id)}/suspend`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  async unsuspendGame(id: string): Promise<OpenGameDetail> {
    const body = await adminFetch<DataEnvelope<OpenGameDetail>>(
      `/admin/open-games/${encodeURIComponent(id)}/unsuspend`,
      { method: 'POST' },
    );
    return body.data;
  },

  async getReports(opts: ListReportsOpts = {}): Promise<AdminOpenGameReportsListResponse> {
    const qs = buildQuery({
      status: opts.status === 'all' ? undefined : (opts.status ?? 'open'),
      page: opts.page ?? 1,
      perPage: opts.perPage ?? 25,
    });
    return adminFetch<AdminOpenGameReportsListResponse>(`/admin/open-game-reports${qs}`);
  },

  async resolveReport(id: string, input: ResolveOpenGameReportInput): Promise<OpenGameReport> {
    const body = await adminFetch<DataEnvelope<OpenGameReport>>(
      `/admin/open-game-reports/${encodeURIComponent(id)}/resolve`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },
};
